import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Eye, Trash2, Upload, Link, Loader2 } from "lucide-react";
import { compressImage, compressPDF } from "lib/utils";

const MAX_CERTS = 10;

const AddCertification = ({ value = [], onChange, bg = "bg-card" }) => {
  const [certs, setCerts] = useState([]);
  const inputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (Array.isArray(value)) {
      setCerts(value);
    }
  }, [value]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []); // Convert FileList to Array
    if (inputRef.current) inputRef.current.value = "";
    if (files.length === 0) return;

    // Check if adding these files exceeds the limit
    if (certs.length + files.length > MAX_CERTS) {
      alert(`You can only have a maximum of ${MAX_CERTS} certifications. You tried to add ${files.length}, but only have room for ${MAX_CERTS - certs.length} more.`);
      return;
    }

    try {
      setIsProcessing(true);

      // Process all selected files
      const processedCerts = await Promise.all(
        files.map(async (file) => {
          let processedFile = file;
          try {
            if (file.type.startsWith("image/")) {
              processedFile = await compressImage(file);
            } else if (file.type === "application/pdf") {
              processedFile = await compressPDF(file);
            }
          } catch (err) {
            console.error("Compression failed for", file.name, err);
            // Fallback to original file if compression fails
          }

          return {
            id: crypto.randomUUID(),
            name: processedFile.name,
            size: (processedFile.size / 1024 / 1024).toFixed(1) + " MB",
            file: processedFile,
            url: URL.createObjectURL(processedFile),
          };
        })
      );

      setCerts((prev) => {
        const updated = [...prev, ...processedCerts];
        onChange?.(updated);
        return updated;
      });
    } catch (error) {
      console.error("Processing failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const removeCert = (id) => {
    setCerts((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      onChange?.(updated);
      return updated;
    });
  };

  const viewCertificate = (cert) => {
    if (!cert?.url) return;
    window.open(cert.url, "_blank");
  };

  return (
    <div className={`w-full border border-border rounded-xl p-5 ${bg}`}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-semibold">
          Certifications ({certs.length}/{MAX_CERTS})
        </p>

        <Button asChild disabled={isProcessing || certs.length >= MAX_CERTS} variant="outline">
          <label className={`${isProcessing || certs.length >= MAX_CERTS ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} flex items-center gap-2`}>
            {isProcessing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
            <span className="text-sm">{isProcessing ? "Processing..." : "Upload Files"}</span>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              className="hidden"
              onChange={handleUpload}
              disabled={isProcessing || certs.length >= MAX_CERTS}
              multiple // <--- THIS ALLOWS MULTIPLE SELECTION
            />
          </label>
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        {certs.map((cert) => (
          <div
            key={cert.id}
            className="border bg-background rounded-xl p-3 flex justify-between items-center text-sm"
          >
            <div className="flex items-center gap-3 overflow-hidden">
              <Link size={16} className="text-muted-foreground flex-shrink-0" />
              <span className="font-medium truncate">{cert.name}</span>
              <span className="text-[10px] bg-muted px-2 py-0.5 rounded text-muted-foreground">
                {cert.size}
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => viewCertificate(cert)}
                className="h-8 w-8"
              >
                <Eye size={16} />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                type="button"
                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => removeCert(cert.id)}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}

        {certs.length === 0 && !isProcessing && (
            <p className="text-center text-xs text-muted-foreground py-4">
                No certifications uploaded yet.
            </p>
        )}
      </div>
    </div>
  );
};

export default AddCertification;
